import React, { useState } from 'react'
import { Copy, Check, AlertCircle, QrCode } from 'lucide-react'
import { Button } from '../ui'
import { APP_TIME_ZONE } from '@/utils/dateFormat'

interface InvitationCodeDisplayProps {
  inviteCode: string
  expiresAt: Date | string
  isValid: boolean
  className?: string
}

export function InvitationCodeDisplay({
  inviteCode,
  expiresAt,
  isValid,
  className = '',
}: InvitationCodeDisplayProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatExpiryDate = (date: Date | string) => {
    const expiryDate = typeof date === 'string' ? new Date(date) : date
    return expiryDate.toLocaleDateString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isExpired = !isValid

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Код приглашения
          </label>
          {isExpired && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
              <AlertCircle className="w-3 h-3 mr-1" />
              Истёк
            </span>
          )}
          {!isExpired && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
              Активен
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded font-mono text-lg font-semibold text-gray-900">
            {inviteCode}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={isExpired}
            className="flex-shrink-0"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Скопировано
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Копировать
              </>
            )}
          </Button>
        </div>

        <p className="mt-2 text-xs text-gray-500">
          Действителен до: {formatExpiryDate(expiresAt)}
        </p>
      </div>

      {!isExpired && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <QrCode className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Как поделиться кодом:</p>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• Отправьте код сотруднику в WhatsApp или Telegram</li>
                <li>• Сотрудник откроет мобильное приложение TCO</li>
                <li>• Введёт этот код для регистрации</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
